import { useStyleRegister } from '@ant-design/cssinjs';
import { Divider, Typography, theme } from 'antd';
import React from 'react';
import rehypeKatex from 'rehype-katex';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import 'katex/dist/katex.min.css';

export const markdownRemarkPlugins = [remarkGfm, remarkMath];
export const markdownRehypePlugins = [rehypeKatex];

const prefixCls = 'kip-markdown';

const isExternalLink = (href = '') => /^https?:\/\//i.test(href);

const mergeClassNames = (...classNames) => classNames.filter(Boolean).join(' ');

export const createMarkdownComponents = () => ({
  a: ({ children, href, node: _node, ...restProps }) => {
    const external = isExternalLink(href);
    return React.createElement(
      Typography.Link,
      {
        ...restProps,
        href,
        rel: external ? 'noopener noreferrer' : undefined,
        target: external ? '_blank' : undefined,
      },
      children,
    );
  },
  hr: ({ node: _node, ...restProps }) => React.createElement(Divider, restProps),
  img: ({ alt, node: _node, src, ...restProps }) =>
    React.createElement('img', {
      ...restProps,
      alt,
      src,
    }),
  table: ({ children, node: _node, ...restProps }) =>
    React.createElement(
      'div',
      { className: `${prefixCls}-table-wrapper` },
      React.createElement('table', restProps, children),
    ),
});

const createMarkdownStyle = (token, tokenPrefixCls) => ({
  [`.${tokenPrefixCls}`]: {
    color: token.colorText,
    fontSize: token.fontSize,
    lineHeight: token.lineHeight,
    overflowWrap: 'break-word',
  },
  [`.${tokenPrefixCls} > :first-child`]: {
    marginBlockStart: 0,
  },
  [`.${tokenPrefixCls} > :last-child`]: {
    marginBlockEnd: 0,
  },
  [`.${tokenPrefixCls} :where(h1, h2, h3, h4, h5, h6)`]: {
    color: token.colorTextHeading,
    fontWeight: token.fontWeightStrong,
    lineHeight: token.lineHeightHeading,
    marginBlock: `${token.marginLG}px ${token.marginSM}px`,
  },
  [`.${tokenPrefixCls} :where(h1)`]: {
    fontSize: token.fontSizeHeading2,
  },
  [`.${tokenPrefixCls} :where(h2)`]: {
    fontSize: token.fontSizeHeading3,
  },
  [`.${tokenPrefixCls} :where(h3)`]: {
    fontSize: token.fontSizeHeading4,
  },
  [`.${tokenPrefixCls} :where(h4, h5, h6)`]: {
    fontSize: token.fontSizeHeading5,
  },
  [`.${tokenPrefixCls} :where(p)`]: {
    marginBlock: `0 ${token.margin}px`,
  },
  [`.${tokenPrefixCls} :where(a)`]: {
    color: token.colorLink,
  },
  [`.${tokenPrefixCls} :where(blockquote)`]: {
    background: token.colorFillQuaternary,
    borderInlineStart: `${token.lineWidthBold}px ${token.lineType} ${token.colorBorder}`,
    color: token.colorTextSecondary,
    marginBlock: `0 ${token.margin}px`,
    marginInline: 0,
    paddingBlock: token.paddingXS,
    paddingInline: token.padding,
  },
  [`.${tokenPrefixCls} :where(ul, ol)`]: {
    marginBlock: `0 ${token.margin}px`,
    paddingInlineStart: token.paddingLG,
  },
  [`.${tokenPrefixCls} :where(li)`]: {
    marginBlock: token.marginXXS,
  },
  [`.${tokenPrefixCls} :where(pre)`]: {
    background: token.colorBgLayout,
    borderRadius: token.borderRadius,
    marginBlock: `0 ${token.margin}px`,
    overflow: 'auto',
    padding: token.padding,
  },
  [`.${tokenPrefixCls} :where(code)`]: {
    background: token.colorFillQuaternary,
    borderRadius: token.borderRadiusSM,
    color: token.colorText,
    fontFamily: token.fontFamilyCode,
    paddingBlock: 1,
    paddingInline: token.paddingXXS,
  },
  [`.${tokenPrefixCls} :where(pre code)`]: {
    background: 'transparent',
    borderRadius: 0,
    padding: 0,
  },
  [`.${tokenPrefixCls} :where(img)`]: {
    maxWidth: '100%',
  },
  [`.${tokenPrefixCls} :where(.ant-image)`]: {
    maxWidth: '100%',
  },
  [`.${tokenPrefixCls} :where(.ant-image-img)`]: {
    maxWidth: '100%',
  },
  [`.${tokenPrefixCls} .${prefixCls}-table-wrapper`]: {
    marginBlock: `0 ${token.margin}px`,
    overflowX: 'auto',
  },
  [`.${tokenPrefixCls} :where(table)`]: {
    border: `${token.lineWidth}px ${token.lineType} ${token.colorBorderSecondary}`,
    borderCollapse: 'collapse',
    width: '100%',
  },
  [`.${tokenPrefixCls} :where(tbody tr:nth-of-type(2n))`]: {
    background: token.colorFillAlter,
  },
  [`.${tokenPrefixCls} :where(th, td)`]: {
    border: `${token.lineWidth}px ${token.lineType} ${token.colorBorderSecondary}`,
    paddingBlock: token.paddingXS,
    paddingInline: token.paddingSM,
    textAlign: 'start',
  },
  [`.${tokenPrefixCls} :where(tbody td)`]: {
    border: `${token.lineWidth}px ${token.lineType} ${token.colorBorderSecondary}`,
  },
  [`.${tokenPrefixCls} :where(th)`]: {
    background: token.colorFillQuaternary,
    color: token.colorTextHeading,
    fontWeight: token.fontWeightStrong,
  },
});

export const useMarkdownStyles = (className) => {
  const { hashId, theme: tokenTheme, token } = theme.useToken();
  const tokenKey = token._tokenKey || hashId || 'default';
  const tokenPrefixCls = `${prefixCls}-${tokenKey}`;

  useStyleRegister(
    {
      hashId,
      layer: {
        name: 'antd',
      },
      path: ['Markdown', prefixCls, tokenKey],
      theme: tokenTheme,
      token,
    },
    () => createMarkdownStyle(token, tokenPrefixCls),
  );

  return mergeClassNames(prefixCls, tokenPrefixCls, className, hashId);
};
